import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sun, Moon, Search, Menu, X, LogOut, User, Mail } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { GlobalSearch } from "@/components/GlobalSearch";

const NAV = [
    { to: "/", label: "Explore" },
    { to: "/encounters", label: "Cases" },
    { to: "/map", label: "Map" },
    { to: "/media-archive", label: "Media Archive" },
    { to: "/community", label: "Community" },
    { to: "/encounters?source_type=historical", label: "Archive" },
    { to: "/leaderboard", label: "Top Researchers" },
    { to: "/about", label: "About" },
    { to: "/pricing", label: "Pricing" },
];

export function Header() {
    const { theme, toggle } = useTheme();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const fetchUnread = () => {
            api.get("/messages/unread-count")
                .then((r) => { if (!cancelled) setUnread(r.data?.unread || 0); })
                .catch(() => {});
        };
        fetchUnread();
        const t = setInterval(fetchUnread, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user]);

    return (
        <header
            className="sticky top-0 z-50 backdrop-blur-xl border-b"
            style={{
                background: theme === "dark" ? "rgba(10,13,17,0.85)" : "rgba(255,255,255,0.88)",
                borderColor: "var(--ok-border)",
            }}
            data-testid="site-header"
        >
            <div className="ok-container flex items-center justify-between h-16">
                <Link to="/" className="flex items-center" data-testid="header-home-link">
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-4 xl:gap-6" data-testid="primary-nav">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            className={({ isActive }) =>
                                `text-xs xl:text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${
                                    isActive ? "text-[var(--ok-accent)]" : "hover:text-[var(--ok-accent)]"
                                }`
                            }
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setSearchOpen(true)}
                        className="inline-flex p-2 rounded-sm hover:bg-[var(--ok-surface)]"
                        aria-label="Search"
                        data-testid="header-search-btn"
                    >
                        <Search className="w-4 h-4" />
                    </button>
                    <button
                        onClick={toggle}
                        className="p-2 rounded-sm hover:bg-[var(--ok-surface)]"
                        aria-label="Toggle theme"
                        data-testid="theme-toggle"
                    >
                        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </button>

                    {user ? (
                        <div className="hidden lg:flex items-center gap-2">
                            <Link
                                to="/messages"
                                className="relative p-2 rounded-sm hover:bg-[var(--ok-surface)]"
                                aria-label="Messages"
                                data-testid="header-messages-btn"
                            >
                                <Mail className="w-4 h-4" />
                                {unread > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 bg-[var(--ok-accent)] text-black text-[10px] font-mono font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="header-unread-badge">
                                        {unread > 9 ? "9+" : unread}
                                    </span>
                                )}
                            </Link>
                            <Link
                                to="/profile"
                                className="ok-btn-outline flex items-center gap-2"
                                data-testid="header-profile-btn"
                            >
                                <User className="w-3.5 h-3.5" />
                                <span className="max-w-[100px] truncate">{user.name}</span>
                            </Link>
                            <button
                                onClick={logout}
                                className="p-2 rounded-sm hover:bg-[var(--ok-surface)]"
                                aria-label="Log out"
                                data-testid="header-logout-btn"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="hidden lg:flex items-center gap-2">
                            <Link to="/login" className="ok-btn-outline" data-testid="header-login-btn">
                                Log In
                            </Link>
                            <Link to="/pricing" className="ok-btn-primary" data-testid="header-join-pro-btn">
                                Join Pro
                            </Link>
                        </div>
                    )}

                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden p-2 rounded-sm hover:bg-[var(--ok-surface)]"
                        aria-label="Menu"
                        data-testid="mobile-menu-btn"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t"
                    style={{ borderColor: "var(--ok-border)", background: "var(--ok-bg)" }}
                >
                    <div className="ok-container py-4 flex flex-col gap-3">
                        {user && (
                            <div className="pb-3 border-b" style={{ borderColor: "var(--ok-border)" }} data-testid="mobile-account-section">
                                <Link
                                    to="/profile"
                                    onClick={() => setOpen(false)}
                                    className="flex items-center gap-3 py-1"
                                    data-testid="mobile-profile-link"
                                >
                                    {user.avatar_url ? (
                                        <img
                                            src={user.avatar_url.startsWith("http") ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`}
                                            alt=""
                                            className="w-10 h-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center">
                                            {(user.name || "?").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{user.name}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Edit profile &amp; settings →</div>
                                    </div>
                                </Link>
                                <div className="flex gap-2 mt-3">
                                    <Link to="/profile" onClick={() => setOpen(false)} className="ok-btn-outline flex-1 text-center text-xs flex items-center justify-center gap-1.5" data-testid="mobile-edit-profile-btn">
                                        <User className="w-3.5 h-3.5" /> Edit Profile
                                    </Link>
                                    <Link to="/messages" onClick={() => setOpen(false)} className="ok-btn-outline flex-1 text-center text-xs flex items-center justify-center gap-1.5 relative" data-testid="mobile-messages-btn">
                                        <Mail className="w-3.5 h-3.5" /> Messages
                                        {unread > 0 && (
                                            <span className="bg-[var(--ok-accent)] text-black text-[10px] font-mono font-bold rounded-full min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center">
                                                {unread > 9 ? "9+" : unread}
                                            </span>
                                        )}
                                    </Link>
                                </div>
                            </div>
                        )}
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                className="text-sm font-medium py-2"
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                        {user ? (
                            <button
                                onClick={() => { logout(); setOpen(false); }}
                                className="ok-btn-outline w-full mt-2"
                            >
                                Log out
                            </button>
                        ) : (
                            <div className="flex gap-2 mt-2">
                                <Link to="/login" onClick={() => setOpen(false)} className="ok-btn-outline flex-1 text-center">
                                    Log In
                                </Link>
                                <Link to="/signup" onClick={() => setOpen(false)} className="ok-btn-primary flex-1 text-center">
                                    Sign Up
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
        </header>
    );
}
